import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { ProgressRing } from '@/components/analytics/charts/ProgressRing';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { PerformanceMetrics } from '@/types/analytics';
import styles from './OverallSuccessRate.module.scss';

export interface OverallSuccessRateProps {
  data: PerformanceMetrics | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Overall Success Rate Widget
 *
 * Displays the user's overall success rate across all questions
 * with a large circular progress indicator and key metrics.
 */
export function OverallSuccessRate({ data, isLoading, error }: OverallSuccessRateProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useChartDimensions(containerRef as any);

  // Calculate ring size based on container width
  const ringSize = Math.min(width * 0.6, 200);

  const isEmpty = !data || data.totalQuestionsAnswered === 0;

  // Determine color based on success rate
  const getColor = (rate: number): string => {
    if (rate >= 90) return 'var(--success)';
    if (rate >= 70) return 'var(--warning)';
    return 'var(--error)';
  };

  console.log("Overall Data", data);
  return (
    <WidgetContainer
      title="Overall Success Rate"
      description="Your performance across all questions"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Start practicing to track your success rate"
      size="medium"
    >
      <div ref={containerRef} className={styles.overallSuccessRate}>
        <div className={styles.overallSuccessRate__ringContainer}>
          <ProgressRing
            value={data?.overallSuccessRate || 0}
            max={100}
            size={ringSize}
            thickness={16}
            color={getColor(data?.overallSuccessRate || 0)}
            showValue
            animate
          />
        </div>

        <div className={styles.overallSuccessRate__stats}>
          <div className={styles.overallSuccessRate__stat}>
            <span className={styles.overallSuccessRate__statValue}>
              {data?.totalQuestionsAnswered || 0}
            </span>
            <span className={styles.overallSuccessRate__statLabel}>Total Questions</span>
          </div>

          <div className={styles.overallSuccessRate__stat}>
            <span className={styles.overallSuccessRate__statValue}>
              {data?.correctAnswers || 0}
            </span>
            <span className={styles.overallSuccessRate__statLabel}>Correct Answers</span>
          </div>

          <div className={styles.overallSuccessRate__stat}>
            <span className={styles.overallSuccessRate__statValue}>
              {data?.averageScore?.toFixed(1) || '0.0'}%
            </span>
            <span className={styles.overallSuccessRate__statLabel}>Avg Score</span>
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
}
